/**
 * HTTP client - a thin fetch wrapper, NOT axios.
 *
 * Why fetch + not axios: one less dependency to audit, the wire format
 * (JSON in/out + Bearer auth) is simple, and the few helpers we need
 * (typed JSON, error shape, optional auth header) fit in <100 lines.
 *
 * Auth: the Bearer token comes from `getAuthToken()` - an injection
 * seam so the real Keycloak OIDC integration (a later P1.x sub-phase)
 * can swap in without touching every call site. For now it reads
 * `sessionStorage["marsad.access_token"]` so a dev can paste a token
 * from the Keycloak login page (docker compose stack) and exercise
 * the API. Production wiring lands when keycloak-js is added.
 *
 * Error shape: every non-2xx response throws ApiError carrying the
 * status + the parsed { detail } body. React Query's `error` is
 * therefore strongly-typed at the call site.
 */

const API_BASE = ""; // relative - the Vite proxy + nginx both forward /v1

/** ROUND 15 A10 -- what a reader gets in place of a gateway's page. */
export const GATEWAY_DETAIL =
  "The server did not answer this time. Nothing was recorded.";

/** ROUND 16 E10 -- a gateway that gave up WAITING (504) is not one that
 * could not reach the server. The request arrived and may have finished:
 * a 5,000-row undo removed every row while the screen said nothing was
 * recorded. This one never says that. */
export const GATEWAY_TIMEOUT_DETAIL =
  "The server took too long to answer. What you asked for may still have happened; check before trying again.";

export class ApiError extends Error {
  public readonly status: number;
  public readonly detail: string;
  // P61.2 (#566): surfaces the Retry-After header value when the
  // backend returns 429 so callers (e.g. AnalyserPanel retry button)
  // can drive a countdown timer. Undefined when the header is absent.
  public readonly retryAfterSeconds: number | undefined;
  /** Round 13 E4 -- the refusal's catalogue key, when it carries one.
   *
   * The backend composes a sentence too, and that sentence is what an
   * API consumer and a log line read. The key is what lets a SCREEN
   * say it in the reader's own language, which a string composed in
   * the backend never can. */
  public readonly code: string | undefined;
  public readonly params: Record<string, string> | undefined;

  constructor(
    status: number,
    detail: string,
    retryAfterSeconds?: number,
    code?: string,
    params?: Record<string, string>,
  ) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.retryAfterSeconds = retryAfterSeconds;
    this.code = code;
    this.params = params;
  }
}

/**
 * Auth token source. The default reads sessionStorage so a dev can
 * paste a Keycloak access token and call the API in isolation. The
 * P1.x Keycloak adapter slot replaces this with an in-memory token
 * managed by the OIDC client + automatic refresh.
 */
let _tokenGetter: () => string | null = () =>
  typeof window === "undefined"
    ? null
    : window.sessionStorage.getItem("marsad.access_token");

/** Override the token source (used by the future Keycloak adapter). */
export function setAuthTokenGetter(getter: () => string | null): void {
  _tokenGetter = getter;
}

/**
 * Round 20 K38 -- refresh the token AHEAD of a request.
 *
 * keycloak-js fires `onTokenExpired` only once the token has lapsed, so
 * a poll landing in between went out with a dead token: the bell's
 * unread count answered 401 and stayed blank until its next poll,
 * every fourteen minutes, during an incident. The adapter registers a
 * refresher; `minValidity` seconds is how long the token must still
 * have, and -1 forces a refresh. It rejects when the session has
 * really ended.
 */
let _tokenRefresher: ((minValidity: number) => Promise<void>) | null = null;

export function setTokenRefresher(
  refresher: ((minValidity: number) => Promise<void>) | null,
): void {
  _tokenRefresher = refresher;
}

/** Public read of the current auth token. Use this when an API call
 *  bypasses the `api.*` helpers (e.g. blob downloads that need the
 *  raw fetch + Authorization header) so it stays in sync with
 *  `setAuthTokenGetter` overrides used by tests + the future
 *  Keycloak adapter. */
export function currentAuthToken(): string | null {
  return _tokenGetter();
}

function authHeaders(): Record<string, string> {
  const token = _tokenGetter();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * D6 -- how long we will wait out a rate limit before giving up.
 *
 * A 429 is the server saying "slow down", and for how long. Anything
 * beyond a few seconds is not something to hold a form open for; that
 * one surfaces to the caller instead of hanging the screen.
 */
const MAX_RETRY_AFTER_SECONDS = 5;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  /** Internal: a 429 is retried ONCE. */
  isRetry = false,
  /** Internal: filled with `X-Total-Count` when the caller wants it
   *  (round 13 C4). Out-parameter rather than a second return type so
   *  every existing call site keeps its shape. */
  captureTotal?: { total: number | null },
): Promise<T> {
  if (_tokenRefresher) {
    // A failure here is left to the server's answer: a lapsed session
    // comes back 401 and is handled below.
    await _tokenRefresher(30).catch(() => undefined);
  }
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...authHeaders(),
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  // exactOptionalPropertyTypes: build the init conditionally so
  // `body` is omitted (not set to undefined) for GET/DELETE.
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const response = await fetch(`${API_BASE}${path}`, init);

  // Round 20 K38 -- a 401 on a session that still holds is a token that
  // lapsed between the check above and the server's: refresh and ask
  // once more, within the same call. Safe on a POST for the reason a
  // 429 is: the request was refused before it was handled. A session
  // that has really ended fails the refresh and keeps its 401.
  if (response.status === 401 && !isRetry && _tokenRefresher) {
    let refreshed = true;
    await _tokenRefresher(-1).catch(() => {
      refreshed = false;
    });
    if (refreshed) {
      return request<T>(method, path, body, true, captureTotal);
    }
  }

  // 204 No Content (DELETE entry) has no body.
  if (response.status === 204) {
    return undefined as T;
  }

  // Try to parse JSON; fall back to text for non-JSON errors (502s,
  // proxy timeouts, etc).
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // ROUND 15 A10 -- a body that is not the product's envelope is a
    // gateway's page or a proxy's text, and it reached a toast as HTML
    // during a redeploy. Nothing in it is for the person; the sentence
    // is, and the code lets the screen say it in their language.
    parsed = response.ok
      ? { detail: text || response.statusText }
      : response.status === 504
        ? { detail: GATEWAY_TIMEOUT_DETAIL, code: "refusal.gateway_timeout" }
        : {
            detail: GATEWAY_DETAIL,
            code: "refusal.gateway_unavailable",
          };
  }

  if (!response.ok) {
    const detail =
      typeof parsed === "object" &&
      parsed !== null &&
      "detail" in parsed &&
      typeof (parsed as { detail: unknown }).detail === "string"
        ? (parsed as { detail: string }).detail
        : response.statusText;
    let retryAfter: number | undefined;
    if (response.status === 429) {
      const raw = response.headers.get("Retry-After");
      if (raw != null) {
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0) retryAfter = n;
      }
      // D6 -- two clicks a second apart hit the limit and lost an
      // action item the operator had typed. Waiting out the window and
      // trying once keeps the form.
      //
      // Safe on a POST: a rate-limited request is refused BEFORE it is
      // handled, so nothing happened that a retry could repeat. That
      // is the whole argument for retrying a non-idempotent call, and
      // it does not extend to any other status.
      const waitFor = retryAfter;
      if (!isRetry && waitFor !== undefined && waitFor <= MAX_RETRY_AFTER_SECONDS) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.max(waitFor, 0.25) * 1000),
        );
        return request<T>(method, path, body, true, captureTotal);
      }
    }
    // ROUND 13 E4 -- the catalogue key and its values, when the
    // refusal carries them. Read defensively: every other error in
    // the product answers without them, and a screen that crashed on
    // a plain 404 would be a worse failure than the one being fixed.
    const envelope = parsed as { code?: unknown; params?: unknown } | null;
    const code =
      typeof envelope?.code === "string" && envelope.code.startsWith("refusal.")
        ? envelope.code
        : undefined;
    const params =
      envelope?.params !== null &&
      typeof envelope?.params === "object" &&
      !Array.isArray(envelope?.params)
        ? Object.fromEntries(
            Object.entries(envelope.params as Record<string, unknown>).map(
              ([key, value]) => [key, String(value)],
            ),
          )
        : undefined;
    throw new ApiError(response.status, detail, retryAfter, code, params);
  }

  if (captureTotal) {
    const raw = response.headers.get("X-Total-Count");
    const n = raw === null ? Number.NaN : Number(raw);
    captureTotal.total = Number.isFinite(n) ? n : null;
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T = void>(path: string) => request<T>("DELETE", path),
  /** Verb chosen at runtime -- the My Work inbox is handed the method
   *  by the composer, because the ceremonies do not share one. */
  request: <T>(method: string, path: string, body?: unknown) =>
    request<T>(method, path, body),
  /**
   * Round 13 C4 -- a GET that also returns how many rows the register
   * actually holds, from the `X-Total-Count` header.
   *
   * A header rather than an envelope: every list endpoint returns a bare
   * array and is consumed by the SPA, the importer's links and
   * `/v1/ext` clients, so wrapping the body would break all of them for
   * a count. `total` is null when the server did not say, which is the
   * honest answer and is NOT the same as zero.
   */
  getList: async <T>(path: string): Promise<{ rows: T; total: number | null }> => {
    const captured: { total: number | null } = { total: null };
    const rows = await request<T>("GET", path, undefined, false, captured);
    return { rows, total: captured.total };
  },
};
